import GolfClubSearch from '@/components/GolfClubSearch';
import HorizontalScrollMedia from '@/components/HorizontalScrollMedia';
import { ImageWithFallback } from '@/components/ui/fallbackImage';
import { IMAGES_BASE_URL } from '@/lib/config';
import { capitalizeCityName, getLocale } from '@/lib/utils';
import { api } from '@/utils/api';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonRouterLink,
  IonSkeletonText,
  IonText,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import { locationOutline, mapOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

export type GolfClub = {
  id: string;
  name: string;
  city: string;
  country: string;
  distance?: number;
  thumbnail: string;
  holes: number;
  rating?: string;      // Rating out of 5
  reviews?: number;     // Number of reviews
  amenities?: string[]; // Available amenities
  description?: string; // Short description
};

const FEATURED_COUNTRIES_WITH_STATES = ['France'];
const FEATURED_COUNTRIES = ['Italy', 'Spain', 'Germany', 'Switzerland', 'Scotland', 'Ireland', 'England', 'Sweden', 'Netherlands', 'Finland'];
interface ClubsExplorerParams {
  country?: string;
  state?: string;
}

import { getRecentlyVisitedClubs, RecentGolfClub } from '@/utils/recentClubs';

const ClubsExplorer: React.FC = () => {
  const { t } = useTranslation();
  const { country, state } = useParams<ClubsExplorerParams>();
  const history = useHistory();
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [recentClubs, setRecentClubs] = useState<RecentGolfClub[]>([]);
  const [filteredClubs, setFilteredClubs] = useState<GolfClub[]>([]);
  const [featuredStates, setFeaturedStates] = useState<Record<string, { name: string, slug: string }[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [pageTitle, setPageTitle] = useState<string>('Where are you golfing ?');

  // Get user location
  const getGeolocation = async () => {
    try {
      // Get location based on platform
      if (Capacitor.isNativePlatform()) {
        // For native platforms, use Capacitor's permission system
        const permissionStatus = await Geolocation.checkPermissions();

        // Request permissions if not granted (handles both 'denied' and 'prompt')
        if (permissionStatus.location !== 'granted') {
          const permissionRequest = await Geolocation.requestPermissions();
          if (permissionRequest.location !== 'granted') {
            console.log('Location permission not granted');
            return;
          }
        }

        console.log('Getting native location...');
        const { coords } = await Geolocation.getCurrentPosition();
        console.log('Location found:', coords.latitude, coords.longitude);
        setLocation({ latitude: coords.latitude, longitude: coords.longitude });
      } else {
        // For web, skip the Capacitor permission request and use browser API directly
        console.log('Using web geolocation API');
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            console.log('Web location found:', coords.latitude, coords.longitude);
            setLocation({ latitude: coords.latitude, longitude: coords.longitude });
          },
          (err) => {
            console.warn('Web Geolocation error:', err);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    } catch (err) {
      console.warn('Geolocation error:', err);
    }
  };

  // Fetch golf clubs
  const fetchGolfClubs = async (query: string = '', countryFilter?: string, stateFilter?: string) => {
    try {
      let searchParams = query ? `query=${encodeURIComponent(query)}` : '';

      // Add location parameters if available
      if (location && !countryFilter) {
        const locationParams = `latitude=${encodeURIComponent(location.latitude)}&longitude=${encodeURIComponent(location.longitude)}`;
        searchParams = searchParams ? `${searchParams}&${locationParams}` : locationParams;
      }

      // Add country filter if provided
      if (countryFilter) {
        const countryParam = `country=${encodeURIComponent(countryFilter)}`;
        searchParams = searchParams ? `${searchParams}&${countryParam}` : countryParam;
      }

      // Add state filter if provided
      if (stateFilter && countryFilter) {
        const stateParam = `state=${encodeURIComponent(stateFilter)}`;
        searchParams = searchParams ? `${searchParams}&${stateParam}` : stateParam;
      }

      const { data } = await api.get(`/golf-clubs?${searchParams}`);
      if (!data) throw new Error('No data received from API');
      return data;
    } catch (error) {
      console.error('Error fetching golf clubs:', error);
      return [];
    }
  };

  // Fetch featured clubs by country
  const fetchFeaturedClubsByCountry = async () => {
    try {
      const statesByCountry: Record<string, { state: string, slug: string }[]> = {};

      // Fetch and process clubs for each featured country
      for (const country of FEATURED_COUNTRIES_WITH_STATES) {
        const { data } = await api.get('/golf-clubs/by-country', {
          params: { country }
        });

        statesByCountry[country] = data;
      }
      return statesByCountry;
    } catch (error) {
      console.error('Error fetching featured clubs:', error);
      return {};
    }
  };

  // Load recently visited clubs
  useEffect(() => {
    const loadRecentClubs = async () => {
      setIsLoadingRecent(true);
      try {
        const recentlyVisited = await getRecentlyVisitedClubs();
        setRecentClubs(recentlyVisited);
      } catch (error) {
        console.error('Error loading recently visited clubs:', error);
      } finally {
        setIsLoadingRecent(false);
      }
    };

    loadRecentClubs();
  }, []);

  // Initialize data
  useEffect(() => {
    const initData = async () => {
      setIsLoading(true);

      // Only get geolocation if we're not filtering by country
      if (!country) {
        await getGeolocation();
      }

      // Fetch featured clubs by country
      const featuredByCountry = await fetchFeaturedClubsByCountry();
      setFeaturedStates(featuredByCountry);

      setIsLoading(false);
    };

    initData();
  }, [country]);

  // Fetch clubs based on parameters
  useEffect(() => {
    const fetchClubs = async () => {
      setIsLoading(true);

      // If we have country/state parameters, fetch filtered clubs
      if (country) {
        // Convert country parameter to proper case for API
        const formattedCountry = country.charAt(0).toUpperCase() + country.slice(1).toLowerCase();

        // Set page title based on filters
        if (state) {
          // Find the state name from the slug
          const stateName = featuredStates[formattedCountry]?.find(s => s.slug === state)?.name || state;
          setPageTitle(`${stateName}, ${formattedCountry}`);
        } else {
          setPageTitle(t('Countries:' + formattedCountry));
        }

        const filtered = await fetchGolfClubs('', formattedCountry, state);
        setFilteredClubs(filtered);
      }
      // Otherwise fetch nearby clubs if location is available
      else if (location) {
        setPageTitle('Nearby Golf Clubs');
        const nearby = await fetchGolfClubs();
        setNearbyClubs(nearby);
      }

      setIsLoading(false);
    };

    fetchClubs();
  }, [location, country, state, featuredStates]);

  // Generate a random rating between 3.5 and 5.0
  const generateRandomRating = () => {
    return (Math.random() * 1.5 + 3.5).toFixed(1);
  };

  // Generate a random number of reviews between 10 and 200
  const generateRandomReviews = () => {
    return Math.floor(Math.random() * 190 + 10);
  };

  // Render club list item in a TripAdvisor/Booking.com style
  const renderClubListItem = (club: GolfClub) => {
    // Generate random data for demo purposes
    const rating = generateRandomRating();
    const reviews = generateRandomReviews();
    const amenities = [
      club.holes + ' ' + t('holes'),
      t('club_rental'),
      t('restaurant'),
      t('pro_shop')
    ];

    return (
      <div className="bg-card rounded-lg shadow-md mb-4 overflow-hidden max-w-4xl mx-auto" key={club.id}>
        <div className="flex flex-col md:flex-row">
          {/* Image section */}
          <div className="md:w-1/3 h-48 md:h-auto relative">
            <ImageWithFallback
              alt={`${club.name} photo`}
              fallbackImage="/assets/images/course_fallback.jpg"
              src={`${IMAGES_BASE_URL}/${club.id}.jpg`}
              className="size-full object-cover"
            />
            {club.distance && (
              <div className="absolute top-2 left-2 bg-primary text-white px-2 py-1 rounded-md text-xs font-semibold">
                {club.distance} km
              </div>
            )}
          </div>

          {/* Content section */}
          <div className="p-4 md:w-2/3 flex flex-col justify-between">
            <div>
              {/* Title and rating */}
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold mb-1">
                  <IonRouterLink routerLink={`/c/${club.id}`} className="text-foreground hover:text-primary">
                    {capitalizeCityName(club.name)}
                  </IonRouterLink>
                </h3>
                <div className="flex items-center">
                  <div className="bg-primary text-white rounded px-1 py-0.5 text-sm font-bold mr-1">
                    {rating}
                  </div>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`text-sm ${i < Math.floor(Number(rating)) ? 'text-yellow-500' : 'text-gray-300'}`}>★</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center text-muted-foreground mb-2">
                <IonIcon icon={locationOutline} className="mr-1 text-sm" />
                <span className="text-sm">{capitalizeCityName(club.city)}, {club.country}</span>
              </div>

              {/* Amenities */}
              <div className="flex flex-wrap gap-2 mb-3">
                {amenities.map((amenity, index) => (
                  <span key={index} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {amenity}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {t('golf_club_description', { name: club.name, holes: club.holes, city: club.city })}
              </p>
            </div>

            {/* Footer with reviews and button */}
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold">{reviews}</span> {t('reviews')}
              </div>
              <IonButton fill="solid" color="primary" size="small" routerLink={`/c/${club.id}`}>
                {t('view_details')}
              </IonButton>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render loading skeleton for featured sections
  const renderSkeleton = () => (
    <IonCard>
      <IonSkeletonText animated style={{ height: '160px' }} />
      <IonCardHeader>
        <IonSkeletonText animated style={{ width: '70%' }} />
        <IonSkeletonText animated style={{ width: '40%' }} />
      </IonCardHeader>
      <IonCardContent>
        <IonSkeletonText animated style={{ width: '30%' }} />
      </IonCardContent>
    </IonCard>
  );

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{pageTitle}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <GolfClubSearch mode='Search' className="bg-secondary" />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Recently visited clubs - Only show on main page */}
        {!country && recentClubs.length > 0 && (
          <div className="p-1">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('recently_visited')}</h2>
            </div>

            {isLoadingRecent ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="w-full">
                    {renderSkeleton()}
                  </div>
                ))}
              </div>
            ) : (
              <HorizontalScrollMedia className="flex gap-4">
                {recentClubs.map(club => {
                  // Format the actual visit date as MMM DD
                  const visitDate = club.visitedAt ? new Date(club.visitedAt) : new Date();
                  const formattedDate = visitDate.toLocaleDateString(getLocale(), {
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <div key={club.id} className="w-full">
                      <div className="bg-card border rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200"
                        onClick={() => history.push(`/c/${club.id}`)}>
                        <div className="flex items-stretch h-24">
                          <ImageWithFallback
                            alt={`${club.name} photo`}
                            fallbackImage="/assets/images/course_fallback.jpg"
                            src={`${IMAGES_BASE_URL}/${club.id}.jpg`}
                            className="size-16 m-4 rounded inset-0 object-cover"
                          />
                          <div className="py-3 flex flex-col justify-between">
                            <div>
                              <h3 className="font-bold text-base line-clamp-1">{capitalizeCityName(club.name)}</h3>
                              <div className="text-xs text-muted-foreground flex items-center mt-1">
                                <IonIcon icon={locationOutline} className="mr-1 text-xs" />
                                {capitalizeCityName(club.city)}
                              </div>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <div className="text-xs text-muted-foreground">
                                {formattedDate} • {club.holes} {t('holes')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </HorizontalScrollMedia>
            )}
          </div>
        )}



        {/* Featured countries with state - Only show on main page */}
        {!country && FEATURED_COUNTRIES_WITH_STATES.map(countryName => (
          <div className="p-1">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">{t(countryName)}</h2>
              <IonButton fill="clear" size="small" routerLink={`/clubs/${countryName.toLowerCase()}`}>
                {t('see_all')}
              </IonButton>
            </div>
            <HorizontalScrollMedia>
              {isLoading ? (
                Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="w-48 h-52 md:w-56 md:h-64">
                      {renderSkeleton()}
                    </div>
                  ))
              ) : featuredStates[countryName]?.length > 0 ? (
                featuredStates[countryName].map((state) => (
                  <IonRouterLink
                    className="cursor-pointer overflow-hidden"
                    key={state.slug}
                    routerLink={`/clubs/${countryName.toLowerCase()}/${state.slug}`}
                  >
                    <div className="relative group">
                      <ImageWithFallback
                        alt={`${state.name} photo`}
                        fallbackImage="/assets/images/fallback.jpg"
                        src={`/assets/images/location/${countryName.toLowerCase()}/${state.slug}.png`}
                        className="w-48 h-52 md:w-56 md:h-64 object-cover rounded-md brightness-90"
                      />
                      <div className="absolute inset-0 shadow-lg flex items-end justify-start p-4">
                        <span className="text-white text-lg font-bold break-words" style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.35), 1px 1px 5px rgba(0,0,0,0.5)' }}>{state.name}</span>
                      </div>
                    </div>
                  </IonRouterLink>
                ))
              ) : (
                <div className="flex justify-center items-center w-full p-4">
                  <IonText color="medium">{t("no_clubs_found")}</IonText>
                </div>
              )}
            </HorizontalScrollMedia>
          </div>
        ))}

        <div className="p-1">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold">{t('travel_in_europe')}</h2>
            <IonButton fill="clear" size="small" routerLink="/clubs">
              {t('see_all')}
            </IonButton>
          </div>

          <HorizontalScrollMedia>
            {isLoading ? (
              Array(8).fill(0).map((_, i) => (
                <div key={i} className="w-56">
                  {renderSkeleton()}
                </div>
              ))
            ) : FEATURED_COUNTRIES.map(country => (
              <IonRouterLink
                className="cursor-pointer overflow-hidden"
                key={country}
                routerLink={`/clubs/${country.toLowerCase()}`}>
                <div className="relative group">
                  <ImageWithFallback
                    alt={`${country} photo`}
                    fallbackImage="/assets/images/fallback.jpg"
                    src={`/assets/images/location/${country.toLowerCase()}.webp`}
                    className="w-48 h-52 md:w-56 md:h-64 object-cover rounded-md brightness-90"
                  />
                  <div className="absolute inset-0 shadow-lg flex items-end justify-start p-6">
                    <span className="text-white text-xl font-bold" style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.35), 1px 1px 5px rgba(0,0,0,0.5)' }}>{t('Countries:' + country)}</span>
                  </div>
                </div>
              </IonRouterLink>
            ))}
          </HorizontalScrollMedia>
        </div>
        {/* Country/State filtered clubs - TripAdvisor/Booking.com style */}
        {country && (
          <div className="py-3">
            {/* Header with title and filters */}
            <div className=" bg-background px-4 py-3 border-b mb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 ">
                {/* Filter options */}
                <div className="flex flex-wrap gap-2">
                  <select className="bg-card border border-input rounded-full p-3 text-sm">
                    <option>{t('holes')}</option>
                    <option>9 {t('holes')}</option>
                    <option>18 {t('holes')}</option>
                    <option>27+ {t('holes')}</option>
                  </select>
                  <select className="bg-card border border-input rounded-full p-3 text-sm">
                    <option>{t('sort_by')}</option>
                    <option>{t('sort_by_name')}</option>
                    <option>{t('sort_by_rating')}</option>
                    <option>{t('sort_by_distance')}</option>
                  </select>
                </div>
              </div>

              {/* Results count */}
              {!isLoading && filteredClubs.length > 0 && (
                <div className="mt-2 text-sm text-muted-foreground">
                  {filteredClubs.length} {t('golf_clubs_found')}
                </div>
              )}
            </div>

            {/* Main content */}
            <div className="px-4">
              {isLoading ? (
                <div className="space-y-4">
                  {Array(3).fill(0).map((_, i) => (
                    <div key={i} className="bg-card rounded-lg shadow-md overflow-hidden animate-pulse">
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/3 h-48 bg-muted"></div>
                        <div className="p-4 md:w-2/3">
                          <div className="h-6 bg-muted rounded w-3/4 mb-3"></div>
                          <div className="h-4 bg-muted rounded w-1/2 mb-3"></div>
                          <div className="flex gap-2 mb-3">
                            <div className="h-6 bg-muted rounded w-16"></div>
                            <div className="h-6 bg-muted rounded w-16"></div>
                            <div className="h-6 bg-muted rounded w-16"></div>
                          </div>
                          <div className="h-4 bg-muted rounded w-full mb-2"></div>
                          <div className="h-4 bg-muted rounded w-full mb-3"></div>
                          <div className="flex justify-between items-center">
                            <div className="h-4 bg-muted rounded w-24"></div>
                            <div className="h-8 bg-muted rounded w-28"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredClubs.length > 0 ? (
                <div className="space-y-4">
                  {filteredClubs.map(club => renderClubListItem(club))}
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center p-12 bg-card rounded-lg shadow-sm">
                  <IonIcon icon={mapOutline} size="large" color="medium" className="text-4xl mb-3" />
                  <IonText color="medium" className="text-lg font-medium text-center mb-2">
                    {t('no_clubs_found_in_area')}
                  </IonText>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    {t('try_different_filters')}
                  </p>
                  <IonButton fill="outline" routerLink="/clubs">
                    {t('explore_other_regions')}
                  </IonButton>
                </div>
              )}
            </div>
          </div>
        )}

      </IonContent>
    </IonPage >
  );
};

export default ClubsExplorer;
